import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress,
  Badge,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  ListItem,
  List,
  Fade,
  Chip,
  Stack,
} from '@mui/material';
import '../../../src/styles/animations.css';
import { styled } from '@mui/material/styles';
import {
  ViewColumn as SplitViewIcon,
  ArrowLeft,
  ArrowRight,
  ArrowLeft as CollapseIcon,
  ArrowRight as ExpandIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon,
  UploadFile as UploadIcon,
  Delete as DeleteIcon,
  DragIndicator as DragHandleIcon,
  DesktopMac as WebViewIcon,
  PhoneAndroid as MobileViewIcon,
  Description as LegacyIcon,
  Code as ClaudeCodeIcon,
} from '@mui/icons-material';
import useNotification from '../../hooks/useNotification';
import documentService from '../../services/documentService';
import visualService from '../../services/visualService';
import EnhancedHtmlRenderer from '../../components/common/EnhancedHtmlRenderer';
// Import full screen functionality
import useFullScreen from '../../hooks/useFullScreen';
import FullScreenButton from '../common/FullScreenButton';
import DevicePreviewFrame from '../common/DevicePreviewFrame';
import DesignActionButtons from '../atoms/DesignActionButtons';
import ArtifactShellRenderer from '../common/ArtifactShellRenderer';

// Fixed divider component
const FixedDivider = styled(Box)(({ theme }) => ({
  width: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'transparent',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    height: '50%',
    width: '4px',
    backgroundColor: theme.palette.divider,
    borderRadius: '2px',
  },
  zIndex: 10
}));

/**
 * A split-view layout component that shows wireframes on the left (75%)
 * and conversation on the right (25%)
 */
const SplitViewWireframes = ({ conversation, children, isStreaming, streamingContent, onConversationUpdate, refreshKey = 0, lastEditedPage = null }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [wireframeHtml, setWireframeHtml] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [wireframeTitle, setWireframeTitle] = useState('AI Generated Wireframe');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [expandedImage, setExpandedImage] = useState(null);
  const [device, setDevice] = useState('desktop'); // 'desktop' | 'tablet' | 'mobile'
  const [isProcessing, setIsProcessing] = useState(false); // Loading state for content transitions
  const [contentKey, setContentKey] = useState(() => Date.now()); // Key for stable iframe rendering, initialized with timestamp to bust cache on page load
  const [showCodeView, setShowCodeView] = useState(false); // Toggle between preview and code view
  const [wireframeMode, setWireframeMode] = useState('legacy'); // 'legacy' for preview mode
  const [currentPageId, setCurrentPageId] = useState(null); // Track which page to display, null = let shell decide
  const [hasWireframesFromApi, setHasWireframesFromApi] = useState(false); // Track if wireframes exist via API check
  const [isCheckingWireframes, setIsCheckingWireframes] = useState(true); // Loading state for initial check

  // Add full screen functionality
  const {
    isFullScreen,
    toggleFullScreen,
    getLeftPanelStyles,
    getRightPanelStyles,
    getContentStyles
  } = useFullScreen();

  // Fixed panel sizes - 75/25 split
  const leftPanelWidth = 0.75;
  const topPanelHeight = 0.75;

  const { showNotification } = useNotification();
  const wireframePanelRef = useRef(null);
  const fileInputRef = useRef(null);
  const wireframeContentRef = useRef('');
  const containerRef = useRef(null);
  const dividerRef = useRef(null);

  // Determine if we should show the split view
  const isWireframesConversation = conversation && (conversation?.taskId || conversation?.task_id) === 'wireframes';

  // Check if wireframes exist via API when component mounts or projectId changes
  useEffect(() => {
    const checkWireframesExist = async () => {
      if (!conversation?.projectId || !isWireframesConversation) {
        setIsCheckingWireframes(false);
        return;
      }

      try {
        setIsCheckingWireframes(true);
        const result = await visualService.listPages(conversation.projectId, 'wireframes');
        const hasPages = result?.data?.pages?.length > 0 || result?.pages?.length > 0;
        console.log('SplitViewWireframes: API check for wireframes:', hasPages ? 'Found' : 'Not found', result);
        setHasWireframesFromApi(hasPages);
      } catch (error) {
        console.log('SplitViewWireframes: Error checking wireframes (may not exist yet):', error);
        setHasWireframesFromApi(false);
      } finally {
        setIsCheckingWireframes(false);
      }
    };

    checkWireframesExist();
  }, [conversation?.projectId, isWireframesConversation, refreshKey]);

  // Watch for conversation metadata changes (wireframes_generated flag)
  // to force iframe re-render when wireframes are created
  useEffect(() => {
    if (conversation?.metadata?.wireframes_generated) {
      // Increment contentKey to force iframe remount
      setContentKey(prev => prev + 1);
      setHasWireframesFromApi(true); // Also update API state
      console.log('SplitViewWireframes: Wireframes generated, forcing iframe re-render');
    }
  }, [conversation?.metadata?.wireframes_generated, conversation?.metadata?.wireframes_url]);

  // Watch for assistant messages that indicate wireframes were created
  // This handles the case where metadata isn't updated but the success message appears
  useEffect(() => {
    if (!conversation?.messages || !isWireframesConversation) return;

    // Check if any assistant message indicates wireframes were created
    const hasWireframeSuccessMessage = conversation.messages.some(msg =>
      msg.role === 'assistant' &&
      (msg.content?.includes('Wireframes created successfully') ||
       msg.content?.includes('files generated'))
    );

    if (hasWireframeSuccessMessage && !hasWireframesFromApi) {
      console.log('SplitViewWireframes: Detected wireframe success message, re-checking API');
      // Re-check the API for wireframes
      const recheckWireframes = async () => {
        try {
          const result = await visualService.listPages(conversation.projectId, 'wireframes');
          const hasPages = result?.data?.pages?.length > 0 || result?.pages?.length > 0;
          if (hasPages) {
            console.log('SplitViewWireframes: Found wireframes via API after success message');
            setHasWireframesFromApi(true);
            setContentKey(prev => prev + 1);
          }
        } catch (error) {
          console.log('SplitViewWireframes: Error re-checking wireframes:', error);
        }
      };
      recheckWireframes();
    }
  }, [conversation?.messages, isWireframesConversation, hasWireframesFromApi, conversation?.projectId]);

  // Watch for refreshKey changes from parent (when pages are edited via VisualLinksPanel)
  useEffect(() => {
    if (refreshKey > 0) {
      const newKey = Date.now();
      setContentKey(newKey);
      console.log('SplitViewWireframes: Page edited, forcing iframe re-render (refreshKey:', refreshKey, ', newContentKey:', newKey, ')');
    }
  }, [refreshKey]);

  // Watch for lastEditedPage changes to show the edited page instead of index
  useEffect(() => {
    if (lastEditedPage?.pageId && lastEditedPage?.taskType === 'wireframes') {
      console.log('SplitViewWireframes: Switching to edited page:', lastEditedPage.pageId);
      setCurrentPageId(lastEditedPage.pageId);
    }
  }, [lastEditedPage]);

  // Extract wireframe HTML from streaming content or latest assistant message
  // with debounce to prevent too frequent updates
  useEffect(() => {
    if (!isWireframesConversation) return;

    // Use a flag to prevent immediate processing while streaming
    let shouldProcess = !isStreaming;
    let timeoutId = null;

    // Process content with a slight delay during streaming to minimize flicker
    const processContent = () => {
      // Get only the latest assistant message
      const assistantMessages = conversation?.messages
        ?.filter(msg => msg.role === 'assistant') || [];

      // Get the latest assistant message or use an empty string
      const latestMessage = assistantMessages.length > 0
        ? assistantMessages[assistantMessages.length - 1].content
        : '';

      // Use the streaming content if available, otherwise use the latest message
      const content = isStreaming && streamingContent ? streamingContent : latestMessage;

      // Store the content for export and other operations
      wireframeContentRef.current = content;

      // Only process if the content has meaningful HTML
      if (content.trim()) {
        try {
          // Check for HTML content using a more specific pattern
          if (content.includes('<!DOCTYPE html>') || content.includes('<html') || content.includes('<body')) {
            // Extract complete HTML from the content
            const htmlStartIndex = content.indexOf('<!DOCTYPE html>') >= 0
              ? content.indexOf('<!DOCTYPE html>')
              : (content.indexOf('<html') >= 0 ? content.indexOf('<html') : content.indexOf('<body'));

            let htmlEndIndex = content.lastIndexOf('</html>');
            if (htmlEndIndex >= 0) {
              htmlEndIndex += 7; // Include the closing </html> tag
            } else {
              htmlEndIndex = content.length;
            }

            const extractedHtml = content.substring(htmlStartIndex, htmlEndIndex);

            // Only update state if the HTML has actually changed and is complete
            if (extractedHtml !== wireframeHtml && extractedHtml.includes('<body')) {
              setWireframeHtml(extractedHtml);
            }
          }
        } catch (error) {
          console.error('Error extracting HTML from content:', error);
        }
      }
    };

    if (isStreaming) {
      // During streaming, debounce the updates to reduce flickering
      clearTimeout(timeoutId);
      timeoutId = setTimeout(processContent, 500); // Wait for more content to accumulate
    } else {
      // When not streaming, process immediately
      processContent();
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [conversation, isStreaming, streamingContent, isWireframesConversation, wireframeHtml]);
  
  // Auto-scroll wireframe panel to bottom when streaming content updates
  useEffect(() => {
    if (isStreaming && wireframePanelRef.current && isWireframesConversation) {
      // Wait a bit to ensure content renders before scrolling
      setTimeout(() => {
        if (wireframePanelRef.current) {
          wireframePanelRef.current.scrollTop = wireframePanelRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [streamingContent, isStreaming, isWireframesConversation]);

  // Note: Resize functionality has been removed in favor of fixed 50/50 split

  // Handler for saving wireframe to database
  const handleSaveWireframe = async () => {
    try {
      setIsSaving(true);
      
      // Get the current HTML content
      const content = wireframeHtml;
      
      if (!content) {
        showNotification({
          message: "No wireframe content to save",
          type: "warning"
        });
        setIsSaving(false);
        return;
      }
      
      // Save the wireframe to the database
      const result = await documentService.saveWireframe(
        conversation.projectId,
        content,
        wireframeTitle,
        // Find the Design section to ensure proper categorization
        undefined, // Let the backend look up the proper section ID
        'WIREFRAMES', // Use the proper document type
        'wireframes' // Use the wireframes task ID
      );
      
      // Check if the result includes a contentUrl for the new file-based approach
      if (result.contentUrl) {
        console.log(`Wireframe saved with URL: ${result.contentUrl}`);
        
        // Update UI with a more detailed success message
        showNotification({
          message: "Wireframe saved successfully and available at a dedicated URL",
          type: "success"
        });
      } else {
        showNotification({
          message: "Wireframe saved successfully",
          type: "success"
        });
      }
      
      setIsSaving(false);
    } catch (error) {
      console.error("Error saving wireframe:", error);
      showNotification({
        message: "Failed to save wireframe: " + (error.message || "Unknown error"),
        type: "error"
      });
      setIsSaving(false);
    }
  };
  
  // Handler for exporting wireframe as HTML file
  const handleExportWireframe = () => {
    try {
      // Get the current HTML content
      const content = wireframeHtml;
      
      if (!content) {
        showNotification({
          message: "No wireframe content to export",
          type: "warning"
        });
        return;
      }
      
      // Create a formatted filename with date
      const date = new Date().toISOString().split('T')[0];
      const filename = `wireframe-${date}.html`;
      
      // Create a blob and download it
      const blob = new Blob([content], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        showNotification({
          message: "Wireframe exported successfully",
          type: "success"
        });
      }, 100);
    } catch (error) {
      console.error("Error exporting wireframe:", error);
      showNotification({
        message: "Failed to export wireframe",
        type: "error"
      });
    }
  };
  
  // Handler for copying wireframe HTML to clipboard
  const handleCopyWireframe = () => {
    try {
      const content = wireframeHtml;
      
      if (!content) {
        showNotification({
          message: "No wireframe content to copy",
          type: "warning"
        });
        return;
      }
      
      // Add the same navigation protection and scripts that are used in the preview
      const enhancedContent = addNavigationProtection(content);
      
      navigator.clipboard.writeText(enhancedContent).then(() => {
        showNotification({
          message: "Wireframe HTML copied to clipboard",
          type: "success"
        });
      });
    } catch (error) {
      console.error("Error copying wireframe:", error);
      showNotification({
        message: "Failed to copy wireframe to clipboard",
        type: "error"
      });
    }
  };
  
  // Handler for file upload (reference images)
  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    
    // Filter for image files only
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length === 0) {
      showNotification({
        message: "Please select image files only",
        type: "warning"
      });
      return;
    }
    
    // Generate data URLs for the images
    const newUploadedFiles = imageFiles.map(file => ({
      name: file.name,
      type: file.type,
      size: file.size,
      url: URL.createObjectURL(file),
      file: file // Keep the original file for potential upload to server
    }));
    
    setUploadedFiles(prev => [...prev, ...newUploadedFiles]);
    
    showNotification({
      message: `${imageFiles.length} image${imageFiles.length !== 1 ? 's' : ''} uploaded`,
      type: "success"
    });
  };
  
  // Handler for removing an uploaded file
  const handleRemoveFile = (index) => {
    setUploadedFiles(prev => {
      const newFiles = [...prev];
      // Revoke object URL to prevent memory leaks
      URL.revokeObjectURL(newFiles[index].url);
      newFiles.splice(index, 1);
      return newFiles;
    });
  };
  
  // Handler for expanding an image
  const handleExpandImage = (file) => {
    setExpandedImage(file);
  };
  
  // Handler for toggling code/preview view
  const handleToggleCodeView = () => {
    setShowCodeView(prev => !prev);
  };
  
  // Update content key when wireframeHtml changes
  useEffect(() => {
    // Generate a stable key based on the content to force proper re-rendering
    if (wireframeHtml) {
      setContentKey(prevKey => prevKey + 1);
    }
  }, [wireframeHtml]);
  

  // Function to add navigation protection to HTML content
  const addNavigationProtection = (htmlContent) => {
    if (!htmlContent) return htmlContent;

    // Check if the content already has the prevention script
    if (htmlContent.includes('preventNavigationScript')) {
      return htmlContent;
    }

    // Add base target and prevention script before the closing body tag if missing
    if (!htmlContent.includes('<base target="_self"')) {
      htmlContent = htmlContent.replace('<head>', '<head>\n  <base target="_self">');
    }    

    // Add screen navigation script if it's not already included
    const screenNavigationScript = `
  <script id="screenNavigationScript">
    // Screen navigation script
    function showScreen(screenId) {
      // First hide all screens
      document.querySelectorAll('.screen').forEach(screen => {
        screen.style.display = 'none';
      });
      
      // Then show the selected screen
      const screenToShow = document.getElementById(screenId);
      if (screenToShow) {
        screenToShow.style.display = 'block';
        console.log('Showing screen:', screenId);
      } else {
        console.error('Screen not found:', screenId);
      }
      
      // Update active state in navigation
      document.querySelectorAll('.screen-nav-item').forEach(item => {
        item.classList.remove('active', 'bg-gray-200', 'font-semibold');
        item.classList.add('bg-gray-100');
      });
      
      // Find and update the active navigation item
      const activeNavItem = document.querySelector(\`.screen-nav-item[data-screen="\${screenId}"]\`);
      if (activeNavItem) {
        activeNavItem.classList.add('active', 'bg-gray-200', 'font-semibold');
        activeNavItem.classList.remove('bg-gray-100');
      }
    }

    // Initialize the first screen on load
    document.addEventListener('DOMContentLoaded', function() {
      // Show the first screen by default
      const firstScreen = document.querySelector('.screen');
      if (firstScreen) {
        showScreen(firstScreen.id);
      }
      
      // Add click handlers to all navigation items
      document.querySelectorAll('.screen-nav-item').forEach(item => {
        const screenId = item.getAttribute('data-screen');
        item.addEventListener('click', function(e) {
          e.preventDefault();
          showScreen(screenId);
        });
      });
    });
  </script>`;

    if (htmlContent.includes('</body>')) {
      return htmlContent.replace('</body>', `${screenNavigationScript}</body>`);
    } else {
      return `${htmlContent}${screenNavigationScript}`;
    }
  };

  // 3-tier fallback rendering:
  // Tier 1: shell.html + page fragments (new architecture) — ArtifactShellRenderer auto-detects via API
  // Tier 2: Full HTML files on disk, loaded via URL in iframe (legacy, existing behavior preserved)
  // Tier 3: HTML extracted from conversation messages, rendered via srcDoc (legacy, existing behavior preserved)
  //
  // NOTE: We always pass both shell props AND legacy props to ArtifactShellRenderer.
  // It tries shell.html first (via API), falls back to legacy if shell not found.
  // This way projects with shell.html work even if metadata.shell_architecture wasn't set.
  const renderWireframePreview = (content, contentUrl = null) => {
    if (!content && !contentUrl && !hasWireframesFromApi) return null;

    const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8003/api').replace(/\/api\/?$/, '');

    // Tier 2 fallback: Legacy content URL (used only if shell.html not found by ArtifactShellRenderer)
    const legacyUrl = hasWireframesFromApi
      ? `${baseUrl}/wireframe_projects/${conversation?.projectId}/wireframes/${currentPageId || 'dashboard'}.html`
      : null;

    // Tier 3 fallback: Inline HTML from conversation messages
    const legacyInlineHtml = content ? addNavigationProtection(content) : null;

    return (
      <DevicePreviewFrame device={device} onDeviceChange={setDevice}>
        <ArtifactShellRenderer
          projectId={conversation?.projectId}
          taskType="wireframes"
          currentPageId={currentPageId}
          contentKey={contentKey}
          onPageChange={(pageId) => setCurrentPageId(pageId)}
          legacyHtmlContent={legacyInlineHtml}
          legacyContentUrl={legacyUrl}
        />
      </DevicePreviewFrame>
    );
  };
  
  // Don't show the split view if this isn't a wireframes conversation
  // Always show split view for wireframes conversations - the left panel will show
  // placeholder message if no wireframes exist yet
  if (!isWireframesConversation) {
    return <>{children}</>;
  }
    const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8003/api').replace(/\/api\/?$/, '');


  // Render the split view
  return (
    <Box 
      ref={containerRef}
      sx={{ 
        display: 'flex', 
        width: '100%', 
        height: '100%',
        flexDirection: { xs: 'column', md: 'row' },
        position: 'relative',
        mt: 4
      }}
    >
      {/* Wireframe preview panel - LEFT SIDE */}
      <Box
        ref={wireframePanelRef}
        sx={{
          ...getRightPanelStyles(sidebarCollapsed),
          width: { xs: '100%', md: isFullScreen ? '100%' : (sidebarCollapsed ? 0 : `${leftPanelWidth * 100}%`) },
          height: { xs: sidebarCollapsed ? 0 : `${topPanelHeight * 100}%`, md: '100%' },
          flexDirection: 'column',
          p: 2,
          borderTop: { xs: 'none', md: 'none' },
          backgroundColor: '#f7f7f7',
          boxSizing: 'border-box',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 0,
            backgroundColor: 'white',
            display: 'flex',
            flexDirection: 'column',
            height: '97%',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            borderRadius: "8px"
          }}
        >
          {/* Header with view toggles and actions */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2
          }}>
            {/* Left: Title */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant="h6" sx={{ color: '#333', fontWeight: 600 }}>
                Wireframes
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {/* Full screen toggle */}
              <DesignActionButtons projectId={conversation?.projectId} taskType="wireframes" />
              <FullScreenButton
                isFullScreen={isFullScreen}
                onToggle={toggleFullScreen}
              />

              {/* Hidden file input for reference images */}
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept="image/*"
                multiple
                onChange={handleFileUpload}
              />
            </Box>
          </Box>

          {/* Reference images section */}
          {uploadedFiles.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ color: '#485869' }}>
                Reference Images ({uploadedFiles.length})
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                gap: 1, 
                flexWrap: 'wrap',
                maxHeight: '120px',
                overflowY: 'auto',
                p: 1,
                border: '1px solid #e0e0e0',
                borderRadius: 0
              }}>
                {uploadedFiles.map((file, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      position: 'relative',
                      border: '1px solid #e0e0e0',
                      borderRadius: 0,
                      overflow: 'hidden'
                    }}
                  >
                    <img 
                      src={file.url} 
                      alt={file.name}
                      style={{ 
                        width: '80px', 
                        height: '80px', 
                        objectFit: 'cover',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleExpandImage(file)}
                    />
                    <IconButton 
                      size="small" 
                      sx={{ 
                        position: 'absolute', 
                        top: 2, 
                        right: 2,
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.9)',
                        },
                        padding: '2px',
                        color: '#485869'
                      }}
                      onClick={() => handleRemoveFile(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          
          {/* Wireframe preview area */}
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              height: '90%', // Ensure it fills the available space
              position: 'relative'
            }}
            className="iframe-container"
          >
            {/* Render wireframes - uses selected page from page list */}
            {/* Check if wireframes exist via API OR metadata flag - show iframe if either is true */}
            {isCheckingWireframes ? (
                  // Show loading while checking for wireframes
                  <Box sx={{
                    p: 3,
                    textAlign: 'center',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '4px'
                  }}>
                    <CircularProgress size={40} sx={{ mb: 2 }} />
                    <Typography color="text.secondary">
                      Checking for wireframes...
                    </Typography>
                  </Box>
                ) : (hasWireframesFromApi || conversation?.metadata?.wireframes_generated) ? (
                  // Display wireframes using DevicePreviewFrame + ArtifactShellRenderer (3-tier fallback)
                  <Box sx={{
                    width: '100%',
                    height: '100%',
                    overflow: 'hidden'
                  }}>
                    {renderWireframePreview(null, null)}
                  </Box>
                ) : wireframeHtml ? (
                  // Legacy mode - show wireframe HTML content if available
                  <>
                    {isStreaming && (
                      <Box className="iframe-loading" sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        zIndex: 10
                      }}>
                        <CircularProgress size={32} />
                      </Box>
                    )}
                    {/* Check if there's a saved document with a file URL */}
                    {conversation &&
                     conversation.savedDocument &&
                     conversation.savedDocument.contentUrl ? (
                      // If we have a saved wireframe URL, use that
                      renderWireframePreview(null, conversation.savedDocument.contentUrl)
                    ) : (
                      // Otherwise use the inline HTML content
                      renderWireframePreview(wireframeHtml)
                    )}
                  </>
                ) : (
                  // No wireframes yet - show placeholder message
                  <Box sx={{
                    p: 3,
                    textAlign: 'center',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '4px'
                  }}>
                    {isStreaming ? (
                      <>
                        <CircularProgress size={40} sx={{ mb: 2 }} />
                        <Typography color="text.secondary" sx={{ textAlign: 'center', width: '100%' }}>
                          Waiting for wireframes to be generated...
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Box sx={{
                          width: 64,
                          height: 64,
                          mb: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#e0e0e0',
                          borderRadius: '8px'
                        }}>
                          <SplitViewIcon sx={{ fontSize: 32, color: '#9e9e9e' }} />
                        </Box>
                        <Typography color="text.secondary" sx={{ textAlign: 'center', width: '100%' }}>
                          No wireframes found in this conversation yet.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                          Wireframes will appear here after generation.
                        </Typography>
                      </>
                    )}
                  </Box>
                )}
          </Box>
        </Paper>
      </Box>

      {/* Horizontal fixed divider (mobile only) */}
      {!sidebarCollapsed && (
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            alignItems: 'center',
            justifyContent: 'center',
            height: '16px',
            width: '100%',
            position: 'relative',
            backgroundColor: 'transparent',
            '&::after': {
              content: '""',
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: '50%',
              height: '4px',
              backgroundColor: theme => theme.palette.divider,
              borderRadius: '2px',
            }
          }}
          aria-label="Divider between panels"
        />
      )}
      
      {/* Fixed vertical divider (desktop only) */}
      {/* {!sidebarCollapsed && (
        <FixedDivider
          ref={dividerRef}
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            height: '100%'
          }}
          aria-label="Divider between panels"
        />
      )} */}
      
      {/* Collapse/Expand button removed */}
      
      {/* Main conversation panel - RIGHT SIDE */}
      <Box sx={{ 
        ...getLeftPanelStyles(sidebarCollapsed),
        width: { xs: '100%', md: isFullScreen ? '0%' : (sidebarCollapsed ? '100%' : `calc(${(1 - leftPanelWidth) * 100}%)`) },
        height: { xs: sidebarCollapsed ? '100%' : `calc(${(1 - topPanelHeight) * 100}% - 16px)`, md: '100%' },
        p: { xs: 1, md: 2 },
        display: isFullScreen ? 'none' : 'block',
      }}>
        {children}
      </Box>
      
      {/* Expanded image dialog */}
      {expandedImage && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            p: 4
          }}
          onClick={() => setExpandedImage(null)}
        >
          <Box
            sx={{
              position: 'relative',
              maxWidth: '90%',
              maxHeight: '90%',
              overflow: 'auto',
              borderRadius: 1,
              backgroundColor: 'white',
              p: 1
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={expandedImage.url}
              alt={expandedImage.name}
              style={{
                maxWidth: '100%',
                maxHeight: 'calc(90vh - 2rem)',
                objectFit: 'contain'
              }}
            />
            <Typography variant="caption" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
              {expandedImage.name}
            </Typography>
            <IconButton
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: 'rgba(255,255,255,0.8)',
                '&:hover': {
                  backgroundColor: 'white',
                },
              }}
              onClick={() => setExpandedImage(null)}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>
      )}

    </Box>
  );
};

export default SplitViewWireframes;
